import {useEffect, useState} from "react";

/**
 * Instalar el admin como app «Fichar» (#13811). Chrome y Edge avisan con `beforeinstallprompt` en
 * cuanto la página es instalable, muchas veces antes de que se monte la pantalla de fichar: por eso
 * se escucha desde el arranque (index.tsx) y se guarda aquí hasta que alguien pulse «Instalar».
 * Safari no tiene ese evento: en iPhone se instala a mano desde Compartir.
 */
interface InstallPromptEvent extends Event {
	prompt: () => Promise<void>;
	userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: InstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach(l => l());

/** iPhone o iPad. El iPad con iPadOS se presenta como Mac: se le distingue por la pantalla táctil. */
export const isIos = () => /iPhone|iPad|iPod/.test(navigator.userAgent)
	|| (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

/** Abierta como app instalada, no en una pestaña del navegador. */
export const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches
	|| (navigator as unknown as { standalone?: boolean }).standalone === true;

export const listenForInstallPrompt = () => {
	window.addEventListener("beforeinstallprompt", e => {
		e.preventDefault();
		deferred = e as InstallPromptEvent;
		notify();
	});
	window.addEventListener("appinstalled", () => {
		deferred = null;
		installed = true;
		notify();
	});
};

/** Si se puede ofrecer el botón de instalar, y la función que lanza el diálogo del navegador. */
export const useInstallPrompt = () => {
	const [, refresh] = useState(0);
	useEffect(() => {
		const l = () => refresh(n => n + 1);
		listeners.add(l);
		return () => {
			listeners.delete(l);
		};
	}, []);

	const install = async () => {
		if (!deferred) {
			return;
		}
		const e = deferred;
		deferred = null;
		await e.prompt();
		await e.userChoice;
		notify();
	};

	return {canInstall: !!deferred && !installed && !isStandalone(), installed: installed || isStandalone(), install};
};
